import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { map, startWith } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';

export interface CompanyMetricVariableConditionFormDialogData {
  company_metric_variable_condition_id: number;
  key_result_title: string;
  acceptable_value: number | null;
  good_value: number | null;
  evaluation: 'higher_is_better' | 'lower_is_better' | 'exact' | null;
  unit: string | null;
  can_edit_variable_condition: boolean;
  fork_on_save?: boolean;
}

@Component({
  selector: 'app-company-metric-variable-condition-form-dialog',
  templateUrl: './company-metric-variable-condition-form-dialog.component.html',
  styleUrl: './company-metric-variable-condition-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    CdkScrollable,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class CompanyMetricVariableConditionFormDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<CompanyMetricVariableConditionFormDialogComponent, boolean>);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  readonly data = inject<CompanyMetricVariableConditionFormDialogData>(MAT_DIALOG_DATA);

  readonly saving = signal(false);
  readonly is_locked = !this.data.can_edit_variable_condition;

  readonly evaluations = [
    { value: 'higher_is_better', label: 'Cuanto más alto, mejor' },
    { value: 'lower_is_better', label: 'Cuanto más bajo, mejor' },
    { value: 'exact', label: 'Valor exacto' },
  ];

  readonly form = this.fb.nonNullable.group({
    acceptable_value: [this.data.acceptable_value ?? 0, [Validators.required]],
    good_value: [this.data.good_value ?? 0, [Validators.required]],
    evaluation: [this.data.evaluation ?? ('higher_is_better' as 'higher_is_better' | 'lower_is_better' | 'exact'), [Validators.required]],
    unit: [this.data.unit ?? '', [Validators.required]],
  });

  readonly unit_suffix = toSignal(
    this.form.controls.unit.valueChanges.pipe(startWith(this.form.controls.unit.value ?? '')).pipe(map(unit => (unit ?? '').trim())),
    { initialValue: (this.data.unit ?? '').trim() }
  );

  constructor() {
    if (this.is_locked) {
      this.form.disable({ emitEvent: false });
    }
  }

  close(): void {
    this.dialog_ref.close(false);
  }

  save(): void {
    if (this.is_locked || this.form.invalid || this.saving()) {
      return;
    }

    this.saving.set(true);
    const value = this.form.getRawValue();
    const request = this.data.fork_on_save
      ? this.api.forkCompanyMetricVariableCondition(this.data.company_metric_variable_condition_id, {
          acceptable_value: value.acceptable_value,
          good_value: value.good_value,
          evaluation: value.evaluation,
          unit: value.unit.trim(),
        })
      : this.api.updateCompanyMetricVariableCondition(this.data.company_metric_variable_condition_id, {
          acceptable_value: value.acceptable_value,
          good_value: value.good_value,
          evaluation: value.evaluation,
          unit: value.unit.trim(),
        });

    request.subscribe({
      next: () => this.dialog_ref.close(true),
      error: () => this.saving.set(false),
    });
  }
}
